/**
 * RuneDefinitions — the shared rune catalogue for the Runes feature.
 *
 * Runes are special powers that add additional strategy to combat. During a
 * game the player buys runes from the IN-GAME RUNE SHOP: the 🪬 Buy Rune
 * button (hand, preparation phase) opens a screen listing EVERY rune in
 * this catalogue with its gold cost, and the player can buy ANY rune for
 * its price. Each rune is buyable ONCE per game (no duplicates — there is
 * no fixed cap on how many different runes a player can own, gold is the
 * constraint). Once purchased, a rune's effect stays active for the rest
 * of the game, and effects STACK (buying a later rune never replaces an
 * earlier one). There is no pre-match rune selection: the main-page "Runes"
 * button is a read-only reference (browse/scroll the catalogue).
 *
 * This file is the SINGLE SOURCE OF TRUTH for rune content, shared between
 * the authoritative backend (database seeding, purchase validation, combat
 * effects) and the frontend (rune shop, read-only main-page reference, the
 * purchased-runes row, combat display). The backend seeds the `runes`
 * SQLite table from RUNE_DEFINITIONS on first start; the client renders the
 * shop and the reference directly from RUNE_DEFINITIONS (no catalogue data
 * crosses the wire — the server re-validates every purchase against the
 * same catalogue).
 */

/**
 * A single rune definition (mirrors the `runes` database table columns).
 */
export interface RuneDefinition {
  /** Stable rune ID (e.g. 'rune_of_power'). Unique across the catalogue. */
  id: string;
  /** Display name (e.g. "Rune of Power"). */
  name: string;
  /** Human-readable effect description (shown in tooltips/overlays). */
  description: string;
  /** Web path to the rune image (e.g. '/rune-art/power.png'). */
  image: string;
  /** Gold cost of purchasing this rune during a game. */
  goldCost: number;
}

/**
 * A player's rune loadout: three rune IDs in PREFERENCE order (the order
 * the player wants to buy them). For BOTS this is the bot profile's rune
 * build (the profile engine buys these, in this order, from the rune shop);
 * for HUMAN players the rune shop is fully open (any rune, any order) and
 * the field is unused by gameplay — it carries DEFAULT_RUNE_LOADOUT.
 */
export type RuneLoadout = [string, string, string];

/** A legacy rune slot number (kept for protocol/DB backward compatibility). */
export type RuneSlot = 1 | 2 | 3;

/** All legacy rune slots in order. */
export const RUNE_SLOTS: readonly RuneSlot[] = [1, 2, 3];

/**
 * The rune catalogue (APPEND new runes here — the database seeding and the
 * frontend pick them up automatically, mirroring the
 * GAME_ABILITY_DEFINITIONS convention). The first four entries are the
 * initial runes (Rebirth/Power/Health/Dead); the rest were added later and
 * are available to any player who assigns them to a loadout slot.
 */
export const RUNE_DEFINITIONS: readonly RuneDefinition[] = [
  {
    id: 'rune_of_rebirth',
    name: 'Rune of Rebirth',
    description:
      'The first friendly minion that dies during combat is resurrected with 1 HP. Triggers once per combat.',
    image: '/rune-art/rebirth.png',
    goldCost: 20,
  },
  {
    id: 'rune_of_power',
    name: 'Rune of Power',
    description: 'All friendly minions have +3 ATK during combat.',
    image: '/rune-art/power.png',
    goldCost: 10,
  },
  {
    id: 'rune_of_health',
    name: 'Rune of Health',
    description: 'All friendly minions have +3 Health during combat.',
    image: '/rune-art/health.png',
    goldCost: 10,
  },
  {
    id: 'rune_of_the_dead',
    name: 'Rune of the Dead',
    description:
      'When the first friendly minion dies during combat, it is replaced by an Undead minion. Triggers once per combat.',
    image: '/rune-art/dead.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_the_necromancer',
    name: 'Rune of the Necromancer',
    description:
      'The Undead joins your draw pile permanently: every card you draw has a 50% chance to be an Undead.',
    image: '/rune-art/necromancer.png',
    goldCost: 20,
  },
  {
    id: 'rune_of_great_fortune',
    name: 'Rune of Great Fortune',
    description: 'When purchased, immediately grants +1 Life.',
    image: '/rune-art/fortune.png',
    goldCost: 10,
  },
  {
    id: 'rune_of_the_warrior',
    name: 'Rune of the Warrior',
    description: 'All Warrior class minions have +10 ATK during combat.',
    image: '/rune-art/warrior.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_the_tank',
    name: 'Rune of the Tank',
    description: 'All Tank class minions have +10 Health during combat.',
    image: '/rune-art/tank.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_healing',
    name: 'Rune of Healing',
    description:
      'All healing abilities (Lifesteal, Heal, Greater Heal, the Healing Rain spell and the LifeBurst keyword) restore double HP during combat.',
    image: '/rune-art/healing.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_holiness',
    name: 'Holy Rune',
    description:
      'All your minions deal 3x damage against Undead class minions during combat.',
    image: '/rune-art/holiness.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_doom',
    name: 'Rune of Doom',
    description:
      'Sudden death starts immediately in your combats (from the very first attack on).',
    image: '/rune-art/doom.png',
    goldCost: 30,
  },
  {
    id: 'rune_of_grief',
    name: 'Rune of Grief',
    description:
      "At the start of combat, your minions' death effects (Explosive, Resurrect, Necromancy, Exploding, SoulLink) trigger as if every one of your minions had just died — they do not actually die.",
    image: '/rune-art/grief.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_doubled_grief',
    name: 'Doubled Grief',
    description:
      "Your minions' death effects (Explosive, Resurrect, Necromancy, Exploding, SoulLink) trigger one extra time when your minions die in combat.",
    image: '/rune-art/doubled_grief.png',
    goldCost: 20,
  },
  {
    id: 'rune_of_light',
    name: 'Rune of Light',
    description:
      'At the start of each combat, all your battlefield minions permanently gain +1/+1 — improving by +1/+1 each time the rune is used (first combat +1/+1, second +2/+2, third +3/+3, and so on).',
    image: '/rune-art/light.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_options',
    name: 'Rune of Options',
    description:
      'When you upgrade a minion, a 4th option is available: an ability that is eligible but not already offered — or +3/+3 when no such ability exists.',
    image: '/rune-art/options.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_overfeed',
    name: 'Over-feed',
    description:
      "Your healing abilities (Heal, Greater Heal, the Healing Rain spell and the LifeBurst keyword) can over-heal: healing that would be capped at a minion's max HP instead raises that max HP by the overflow (5 max + 5 heal = 10). Temporary — it lasts only for this combat and never persists. LifeBurst over-heals ONLY your own minions (the enemy side stays capped). Lifesteal never over-heals, but it can heal up to the raised max HP.",
    image: '/rune-art/overfeed.png',
    goldCost: 20,
  },
  {
    id: 'rune_of_boom_resistant',
    name: 'Boom Resistant',
    description:
      "Your minions don't take damage from the Explosive or Exploding abilities.",
    image: '/rune-art/boom_resistant.png',
    goldCost: 10,
  },
  {
    id: 'rune_of_high_stakes',
    name: 'Rune of High Stakes',
    description:
      'When you upgrade a minion, the +5 Health and +5 Attack options are replaced by a single High Stakes option: +10 Health AND +10 Attack, with a 10% × level chance (level 1: 10%, level 2: 20%, ...) to DESTROY the minion instead. If destroyed, it is removed without any compensation. Ability options are unaffected.',
    image: '/rune-art/Rune_5.png',
    goldCost: 20,
  },
  {
    id: 'rune_of_decay',
    name: 'Rune of Decay',
    description:
      'When a friendly minion dies that is not of the class Swarmer and does not have the SwarmLord keyword, a Swarmer is summoned and attacks immediately.',
    image: '/rune-art/Rune_1.png',
    goldCost: 20,
  },
  {
    id: 'rune_of_the_swarm',
    name: 'Rune of the Swarm',
    description:
      'Your Swarmers spawn with the Improved keyword (+1/+1 for every Undead you have played or summoned).',
    image: '/rune-art/Rune_4.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_the_mage',
    // Renamed (task 197): "Rune of the Mage" → "Spellmakers Rune" (the
    // id stays stable — purchases/saves/tests key on the id).
    name: 'Spellmakers Rune',
    description:
      'Spells are added to your draw pile: every card you draw is equally likely to be a random level 1 spell as to be any specific class (in COOP, your teammate\u2019s draws benefit too).',
    image: '/rune-art/Rune_11.png',
    goldCost: 5,
  },
  {
    id: 'rune_of_knowledge',
    name: 'Rune of Knowledge',
    description:
      'At the start of each combat, cast a random spell of any level. If it is a minion-target spell, it targets a random friendly minion (if you have one on the board).',
    image: '/rune-art/Rune_14.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_greed',
    name: 'Rune of Greed',
    description:
      'At the start of your turn, draw one extra card. (In COOP, your teammate\u2019s turn draws benefit too.)',
    image: '/rune-art/Rune_7.png',
    goldCost: 7,
  },
  {
    id: 'rune_of_sacrifice',
    name: 'Sacrificial Rune',
    description:
      'Whenever you draw a card that does not fit in your hand, all your battlefield minions permanently gain +5 HP and +5 attack, and a random card in your hand is destroyed.',
    image: '/rune-art/Rune_8.png',
    goldCost: 15,
  },
  {
    id: 'rune_of_swarm_food', // RUNE_SWARM_FOOD_ID (declared below the catalogue — same convention as the other entries)
    name: 'Swarm Food',
    description:
      'Every time you sell a card give a random card on your battlefield +2hp and +2 attack',
    image: '/rune-art/Rune_17.png',
    goldCost: 16,
  },
];

// ─── Rune ID constants (shared by backend combat logic and tests) ─────────

export const RUNE_REBIRTH_ID = 'rune_of_rebirth';
export const RUNE_POWER_ID = 'rune_of_power';
export const RUNE_HEALTH_ID = 'rune_of_health';
export const RUNE_DEAD_ID = 'rune_of_the_dead';
export const RUNE_NECROMANCER_ID = 'rune_of_the_necromancer';
export const RUNE_GREAT_FORTUNE_ID = 'rune_of_great_fortune';
export const RUNE_WARRIOR_ID = 'rune_of_the_warrior';
export const RUNE_TANK_ID = 'rune_of_the_tank';
export const RUNE_HEALING_ID = 'rune_of_healing';
export const RUNE_HOLY_ID = 'rune_of_holiness';
export const RUNE_DOOM_ID = 'rune_of_doom';
export const RUNE_GRIEF_ID = 'rune_of_grief';
export const RUNE_DOUBLED_GRIEF_ID = 'rune_of_doubled_grief';
export const RUNE_LIGHT_ID = 'rune_of_light';
export const RUNE_OPTIONS_ID = 'rune_of_options';
export const RUNE_OVERFEED_ID = 'rune_of_overfeed';
export const RUNE_BOOM_RESISTANT_ID = 'rune_of_boom_resistant';
export const RUNE_HIGH_STAKES_ID = 'rune_of_high_stakes';
export const RUNE_DECAY_ID = 'rune_of_decay';
export const RUNE_SWARM_ID = 'rune_of_the_swarm';
export const RUNE_MAGE_ID = 'rune_of_the_mage';
export const RUNE_KNOWLEDGE_ID = 'rune_of_knowledge';
export const RUNE_GREED_ID = 'rune_of_greed';
export const RUNE_SACRIFICIAL_ID = 'rune_of_sacrifice';
/** Stable rune ID for Swarm Food (see RUNE_DEFINITIONS). */
export const RUNE_SWARM_FOOD_ID = 'rune_of_swarm_food';
/**
 * The permanent stat bonus Swarm Food grants to a random battlefield minion
 * on every minion sale (+2 HP AND +2 attack — the gainHp path honors the
 * Grandmaster's permanent 1 HP cap; Teacher intercepts the gain and
 * redirects it to its neighbors, the Heirloom convention).
 */
export const RUNE_SWARM_FOOD_BONUS_STAT = 2;

// ─── Rune effect constants ────────────────────────────────────────────────

/**
 * Rune of Power: attack bonus applied to EVERY friendly combat copy for the
 * duration of each combat while the rune is active.
 */
export const RUNE_POWER_ATK_BONUS = 3;

/**
 * Rune of Health: HP bonus applied to EVERY friendly combat copy (current
 * HP and the heal cap) for the duration of each combat while the rune is
 * active.
 */
export const RUNE_HEALTH_HP_BONUS = 3;

/**
 * Rune of Rebirth: the resurrected minion returns with this much HP.
 * Matches the Resurrect ability convention (1 HP).
 */
export const RUNE_REBIRTH_HP = 1;

/**
 * Rune of the Necromancer: every card drawn by the owner has this chance
 * (0.5 = 50%) of being an Undead instead of a random pool card. The Undead
 * is a real card — it can be played, sold and upgraded like any other.
 */
export const RUNE_NECROMANCER_UNDEAD_CHANCE = 0.5;

/**
 * Rune of Great Fortune: Life granted once, immediately on purchase
 * (a purchase-time effect, not a combat effect).
 */
export const RUNE_GREAT_FORTUNE_LIFE_BONUS = 1;

/**
 * Rune of the Warrior: attack bonus applied to every friendly WARRIOR-class
 * combat copy for the duration of each combat while the rune is active.
 */
export const RUNE_WARRIOR_ATK_BONUS = 10;

/**
 * Rune of the Tank: HP bonus applied to every friendly TANK-class combat
 * copy (current HP and the heal cap) for the duration of each combat while
 * the rune is active.
 */
export const RUNE_TANK_HP_BONUS = 10;

/**
 * Rune of Healing: HP-restoring abilities (Lifesteal, Heal) restore this
 * many times their normal amount for the owner's minions during combat.
 * (Killer is a full heal — the cap already binds, so it is unaffected.)
 */
export const RUNE_HEALING_MULTIPLIER = 2;

/**
 * Holy Rune: attack damage dealt BY the owner's minions TO Undead-class
 * targets is multiplied by this factor during combat.
 */
export const RUNE_HOLY_UNDEAD_DAMAGE_MULTIPLIER = 3;

/**
 * Rune of Doom: sudden-death threshold while the rune is active — 0 means
 * the escalating sudden-death damage starts from the very first attack of
 * the combat (damage = attack number).
 */
export const RUNE_DOOM_SUDDEN_DEATH_THRESHOLD = 0;

/**
 * Rune of Light: the permanent stat bonus granted to every battlefield
 * minion at the start of a combat is the rune's USE COUNT in this game
 * times this step (first combat +1/+1, second +2/+2, third +3/+3, ...).
 */
export const RUNE_LIGHT_BASE_BONUS = 1;

/**
 * Rune of Options: the stat fallback of the 4th upgrade option (+N HP and
 * +N ATK) when no un-offered eligible ability exists for the minion.
 */
export const RUNE_OPTIONS_BONUS_STAT = 3;

/**
 * Rune of High Stakes: the +N HP AND +N ATK stat gain of the High Stakes
 * upgrade option (the single option that REPLACES the +5 HP and +5 ATK
 * options while the rune is active).
 */
export const HIGH_STAKES_STAT_BONUS = 10;

/**
 * Rune of High Stakes: the destroy-chance step PER MINION LEVEL — a level-N
 * minion has N × 10% chance that the High Stakes upgrade DESTROYS it
 * instead of applying the stat gain (level 1: 10%, level 2: 20%, ...).
 */
export const HIGH_STAKES_DESTROY_CHANCE_PER_LEVEL = 0.1;

/**
 * The chance (0..1) that a High Stakes upgrade of a level-`level` minion
 * DESTROYS the minion instead of applying the +10/+10 — 10% × level,
 * capped at 1. Shared by the authoritative server roll (GameSessionService),
 * the upgrade-options display (DevServer) and the client fallback render
 * (UpgradeChoiceUI), so the number shown and the number rolled can never
 * diverge.
 */
export function highStakesDestroyChance(level: number): number {
  return Math.min(1, Math.max(0, level) * HIGH_STAKES_DESTROY_CHANCE_PER_LEVEL);
}

/**
 * Sacrificial Rune (task 186): the PERMANENT stat bonus granted to EVERY
 * battlefield minion when the rune's overdraw trigger fires (a draw that
 * does not fit in the hand). +N HP and +N attack, per minion.
 */
export const RUNE_SACRIFICIAL_BONUS_STAT = 5;

// ─── Default loadout (guests/bots + new users without stored rows) ────────

/**
 * The default rune preference order (cheap ATK buff, cheap HP buff, the big
 * investment).
 *
 * Used for:
 *  - HUMAN players and guests as the `PlayerState.runeLoadout` default
 *    (unused by gameplay — the rune shop is fully open to them);
 *  - `normalizeRuneLoadout` slot fallbacks (defensive normalization).
 */
export const DEFAULT_RUNE_LOADOUT: RuneLoadout = [
  RUNE_POWER_ID,
  RUNE_HEALTH_ID,
  RUNE_REBIRTH_ID,
];

// ─── Lookup helpers ───────────────────────────────────────────────────────

/**
 * Returns the rune definition for the given ID, or null when unknown.
 */
export function getRuneById(id: string): RuneDefinition | null {
  return RUNE_DEFINITIONS.find((r) => r.id === id) ?? null;
}

/**
 * True when the given ID is a known rune ID.
 */
export function isRuneId(id: unknown): id is string {
  return typeof id === 'string' && RUNE_DEFINITIONS.some((r) => r.id === id);
}

/**
 * Resolves a raw three-rune preference list (bots: the profile's rune build)
 * into a complete RuneLoadout: unknown/missing slots fall back to their
 * default rune. This is the single normalization point used by the backend
 * when creating a match, so every downstream consumer (the bot engine) sees
 * a valid, complete list.
 */
export function normalizeRuneLoadout(raw: readonly string[] | null | undefined): RuneLoadout {
  return RUNE_SLOTS.map((slot) => {
    const id = raw?.[slot - 1];
    return isRuneId(id) ? id : DEFAULT_RUNE_LOADOUT[slot - 1];
  }) as RuneLoadout;
}

/**
 * The index (0-based slot position) of a stage within a loadout.
 */
export function slotIndex(slot: RuneSlot): number {
  return slot - 1;
}

/**
 * Validates that a value is a rune slot (1, 2 or 3).
 */
export function isRuneSlot(value: unknown): value is RuneSlot {
  return value === 1 || value === 2 || value === 3;
}
